import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Select,
  Button,
  Text,
  Divider,
  FormControl,
  Spacer,
  Center,
  useColorModeValue,
} from "@chakra-ui/react";
import { Line } from "react-chartjs-2";
import { Chart, registerables, ChartOptions } from "chart.js";
import { parseISO, getDaysInMonth, format, getMonth, getYear } from "date-fns";
import CustomDropdown from "../../../components/CustomDropdown";

// register Chart.js components
Chart.register(...registerables);

interface TimesheetEntry {
  date: string;
  hours: string;
  project: string;
  status: string;
}

interface Project {
  id: string;
  name: string;
}

interface ProjectViewProps {
  timesheets: TimesheetEntry[];
  assignedProjects?: Project[];
}

const ProjectView: React.FC<ProjectViewProps> = ({
  timesheets,
  assignedProjects = [],
}) => {
  // Only use assigned projects, no mixing with timesheet projects
  const projects = useMemo(() => {
    return [...assignedProjects].sort((a, b) => a.name.localeCompare(b.name));
  }, [assignedProjects]);

  const [selectedProject, setSelectedProject] = useState<string>("");

  // Set initial selected project when projects are loaded
  useEffect(() => {
    if (projects.length > 0) {
      // Find the Requirement Analysis project or use the first project
      const requirementProject = projects.find(
        (p) => p.name === "Requirement Analysis"
      );
      setSelectedProject(requirementProject?.name || projects[0].name);
    }
  }, [projects]);

  const viewOptions = ["week", "month", "year", "overall"] as const;
  const [view, setView] = useState<(typeof viewOptions)[number]>("month");

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<number>(
    new Date().getMonth() + 1
  );

  // Theme-aware colors (gold + dark/light)
  const axisLabelColor = useColorModeValue("#374151", "#E5E7EB");
  const gridColor = useColorModeValue("rgba(107,114,128,0.2)", "rgba(75,85,99,0.35)");
  const textColor = useColorModeValue("#111827", "#FFFFFF");
  const goldBorder = "#B59143";
  const goldFill = "rgba(251,210,130,0.20)";
  const goldPoint = "#B59143";
  const outlineHoverBg = useColorModeValue("rgba(181,145,67,0.08)", "rgba(251,210,130,0.10)");
  const isDarkDropdown = useColorModeValue(false, true);

  // filter and parse hours
  const parseHours = (h: string) => {
    const n = parseFloat(h);
    return isNaN(n) ? 0 : Math.min(n, 24);
  };

  // group data by view
  // put this near parseHours (top of file)
  const PERIOD_LABELS = {
    month: "day",
    week: "week",
    year: "month",
    overall: "year",
  } as const;

  const { labels, periodValues, cumulativeValues, totalHours } = useMemo(() => {
    // filter for selected project + approved statuses (fixed precedence)
    const projectTimesheets = timesheets.filter((entry: TimesheetEntry) => {
      const projectMatch = entry.project === selectedProject;
      const s = entry.status?.toLowerCase?.() ?? "";
      const ok = s === "approved" || s === "approve";
      return projectMatch && ok;
    });

    let lbls: string[] = [];
    const bucket: Record<string, number> = {};

    // Get current date info
    const now = new Date();
    const currentDay = now.getDate();
    const currentWeek = Math.ceil(currentDay / 7);
    const currentMonthIndex = now.getMonth(); // 0-based
    const currentYearValue = now.getFullYear();

    if (view === "month") {
      const daysInMonth = getDaysInMonth(new Date(selectedYear, selectedMonth - 1));
      const isCurrentMonth = selectedYear === currentYearValue && selectedMonth === (currentMonthIndex + 1);

      // Always show all days
      lbls = Array.from({ length: daysInMonth }, (_, i) => String(i + 1));

      projectTimesheets.forEach((e) => {
        const d = parseISO(e.date);
        if (d.getFullYear() === selectedYear && d.getMonth() + 1 === selectedMonth) {
          const key = String(d.getDate());
          bucket[key] = (bucket[key] ?? 0) + parseHours(e.hours);
        }
      });
    } else if (view === "week") {
      // Always show all 5 weeks
      lbls = ["Week 1", "Week 2", "Week 3", "Week 4", "Week 5"];

      projectTimesheets.forEach((e) => {
        const d = parseISO(e.date);
        if (d.getFullYear() === selectedYear && d.getMonth() + 1 === selectedMonth) {
          const key = `Week ${Math.ceil(d.getDate() / 7)}`;
          bucket[key] = (bucket[key] ?? 0) + parseHours(e.hours);
        }
      });
    } else if (view === "year") {
      // Always show all 12 months
      lbls = Array.from({ length: 12 }, (_, i) => format(new Date(0, i), "MMM"));

      projectTimesheets.forEach((e) => {
        const d = parseISO(e.date);
        if (d.getFullYear() === selectedYear) {
          const key = format(new Date(0, d.getMonth()), "MMM");
          bucket[key] = (bucket[key] ?? 0) + parseHours(e.hours);
        }
      });
    } else {
      // overall by year
      const years = Array.from(
        new Set(projectTimesheets.map((e) => getYear(parseISO(e.date))))
      ).sort((a, b) => a - b);
      lbls = years.length ? years.map(String) : [String(selectedYear)];

      projectTimesheets.forEach((e) => {
        const key = String(getYear(parseISO(e.date)));
        bucket[key] = (bucket[key] ?? 0) + parseHours(e.hours);
      });
    }

    const periodVals = lbls.map((l) => bucket[l] ?? 0);

    // Calculate cumulative values but only up to current period
    const cumulativeVals: number[] = [];
    let run = 0;

    for (let i = 0; i < periodVals.length; i++) {
      const shouldShowData = (() => {
        if (view === "month") {
          const isCurrentMonth = selectedYear === currentYearValue && selectedMonth === (currentMonthIndex + 1);
          return !isCurrentMonth || (i + 1) <= currentDay;
        } else if (view === "week") {
          const isCurrentMonth = selectedYear === currentYearValue && selectedMonth === (currentMonthIndex + 1);
          return !isCurrentMonth || (i + 1) <= currentWeek;
        } else if (view === "year") {
          const isCurrentYear = selectedYear === currentYearValue;
          return !isCurrentYear || i <= currentMonthIndex;
        } else {
          // overall view
          return parseInt(lbls[i]) <= currentYearValue;
        }
      })();

      if (shouldShowData) {
        run += periodVals[i];
        cumulativeVals.push(run);
      } else {
        cumulativeVals.push(null as any); // null values won't be plotted
      }
    }

    return {
      labels: lbls,
      periodValues: periodVals,
      cumulativeValues: cumulativeVals,
      totalHours: run,
    };
  }, [selectedProject, selectedYear, selectedMonth, view, timesheets]);


  const chartData = {
    labels,
    datasets: [
      {
        label: `Cumulative Hours - ${selectedProject}`,
        data: cumulativeValues,
        borderColor: goldBorder,
        backgroundColor: goldFill,
        pointBackgroundColor: goldPoint,
        pointRadius: 1.5, // Made even smaller
        pointHoverRadius: 3, // Made smaller for hover
        tension: 0.2,
        fill: true,
        borderWidth: 1, // Made thinner from default (usually 3)
      },
    ],
  };


  const periodNoun = PERIOD_LABELS[view]; // "day" | "week" | "month" | "year"

  const chartOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: "top", labels: { color: axisLabelColor } },
      tooltip: {
        enabled: true,
        mode: "index",
        intersect: false,
        callbacks: {
          title: (items) => `${items[0].label} (${view.toUpperCase()})`,
          label: (ctx) => {
            const i = ctx.dataIndex;
            const cum = cumulativeValues[i] ?? 0;
            const per = periodValues[i] ?? 0;
            return [`Cumulative: ${cum.toFixed(1)} h`, `This ${periodNoun}: ${per.toFixed(1)} h`];
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text:
            view === "month"
              ? "Days of the Month"
              : view === "week"
                ? "Weeks of the Month"
                : view === "year"
                  ? "Months"
                  : "Years",
          color: axisLabelColor,
        },
        ticks: { color: axisLabelColor },
        grid: { display: true, color: gridColor },
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: "Hours Worked (Cumulative)", color: axisLabelColor },
        ticks: { color: axisLabelColor },
        grid: { color: gridColor },
        // let Chart.js autoscale; remove fixed max/stepSize
      },
    },
    interaction: { intersect: false, mode: "index" },
  };


  // Check if no projects (moved inside unconditional render)
  const hasProjects = projects.length > 0;

  if (!hasProjects) {
    return (
      <Center height="100%">
        <Text fontSize="lg" color="gray.500">
          No projects assigned to this user.
        </Text>
      </Center>
    );
  }

  return (
    <VStack spacing={4} align="stretch" width="100%">
      {/* Project selector */}
      <FormControl maxW="300px">
        <CustomDropdown
          value={selectedProject}
          onChange={setSelectedProject}
          isDark={isDarkDropdown}
          options={projects.map((proj) => ({ value: proj.name, label: proj.name }))}
          className="w-full px-2 py-2 text-sm rounded-md border"
          style={{ height: "36px", minWidth: "180px" }}
        />
      </FormControl>

      {/* View & period controls */}
      <HStack wrap="wrap" spacing={4}>
        {viewOptions.map((opt) => (
          <Button
            key={opt}
            size="sm"
            variant={view === opt ? "gold" : "outline"}
            borderColor={goldBorder}
            color={view === opt ? "white" : goldBorder}
            bg={view === opt ? undefined : "transparent"}
            _hover={{ bg: view === opt ? undefined : outlineHoverBg, borderColor: goldBorder, color: view === opt ? "white" : goldBorder }}
            _active={{ bg: view === opt ? undefined : outlineHoverBg }}
            onClick={() => setView(opt)}
          >
            {opt.charAt(0).toUpperCase() + opt.slice(1)}
          </Button>
        ))}
        <Spacer />
        <HStack>
          <Text color={textColor}>Year:</Text>
          <CustomDropdown
            value={String(selectedYear)}
            onChange={(value) => setSelectedYear(Number(value))}
            isDark={isDarkDropdown}
            options={Array.from({ length: 5 }, (_, i) => {
              const y = currentYear - 4 + i;
              return { value: String(y), label: String(y) };
            })}
            className="w-24 px-2 py-1 text-sm rounded-md border"
            style={{ height: "32px", minWidth: "88px" }}
          />
        </HStack>

        {(view === "week" || view === "month") && (
          <HStack>
            <Text color={textColor}>Month:</Text>
            <CustomDropdown
              value={String(selectedMonth)}
              onChange={(value) => setSelectedMonth(Number(value))}
              isDark={isDarkDropdown}
              options={Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: format(new Date(0, i), "MMMM") }))}
              className="w-36 px-2 py-1 text-sm rounded-md border"
              style={{ height: "32px", minWidth: "118px" }}
            />
          </HStack>
        )}
      </HStack>

      <Divider />

      {/* Total Hours */}
      <Center>
        <Text fontWeight="bold">
          {view === "year" || view === "overall"
            ? `Total Days: ${totalHours.toFixed(0)}`
            : `Total Hours: ${totalHours.toFixed(1)}`}
        </Text>
      </Center>

      {/* Chart */}
      <Box height="40vh" width="100%">
        <Line data={chartData} options={chartOptions} />
      </Box>
    </VStack>
  );
};

export default ProjectView;